import React from 'react'
import Orb from '../Orb'
import { Heading, Lead } from '../Text'
import styles from './styles.module.scss'

export interface InfoCardAction {
    label: string
    // A link (opens in a new tab) when given, otherwise a button
    href?: string
    onClick?: () => void
    ref?: React.Ref<HTMLButtonElement>
}

interface InfoCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    // Optional, e.g. left out when the graphic already names it (a logo)
    title?: React.ReactNode
    // id on the title, for a dialog's aria-labelledby
    titleId?: string
    children: React.ReactNode
    // Graphic up top; the silver Orb unless given (e.g. NoMark for a "no")
    visual?: React.ReactNode
    action: InfoCardAction
    // A second, quieter button under the first (e.g. Close under "Visit site")
    secondaryAction?: InfoCardAction
}

const ActionButton: React.FC<{ action: InfoCardAction; className: string }> = ({ action, className }) =>
    action.href ? (
        <a className={className} href={action.href} target="_blank" rel="noreferrer" onClick={action.onClick}>
            {action.label}
        </a>
    ) : (
        <button ref={action.ref} type="button" className={className} onClick={action.onClick}>
            {action.label}
        </button>
    )

/**
 * A 4:5 card: a silver graphic up top (the orb by default), title and
 * description in the middle, the button(s) at the bottom. One button is
 * outlined; with two, the first is filled and the second outlined beneath it.
 * Animates in on mount. Used inline (eligibility result) and in InfoModal.
 */
const InfoCard: React.FC<InfoCardProps> = ({
    title,
    titleId,
    children,
    visual,
    action,
    secondaryAction,
    className,
    ...rest
}) => (
    <div className={`${styles.card} ${className || ''}`} {...rest}>
        <div className={styles.visual}>{visual ?? <Orb size={88} />}</div>
        <div className={styles.text}>
            {title && (
                <Heading as="h2" align="center" className={styles.title}>
                    <span id={titleId}>{title}</span>
                </Heading>
            )}
            <Lead align="center" className={styles.description}>
                {children}
            </Lead>
        </div>
        <div className={styles.actions}>
            <ActionButton
                action={action}
                className={`${styles.actionButton} ${secondaryAction ? styles.primary : ''}`}
            />
            {secondaryAction && <ActionButton action={secondaryAction} className={styles.actionButton} />}
        </div>
    </div>
)

export default InfoCard
